import { tenantStore } from "@/stores/tenantStore";
import type {
  Agenda,
  AgendaItem,
  AgendaModule,
  Document,
  Meeting,
} from "@repo/types";
import { useContext, useRef } from "react";
import { TrackedNewObjectsContext } from "./TrackedNewObjectsProvider";
import { meetingRepo } from "@/repositories/meetingRepo";
import { agendaRepo } from "@/repositories/agendaRepo";
import { agendaModuleRepo } from "@/repositories/agendaModuleRepo";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { documentRepo } from "@/repositories/documentRepo";

export type TrackedNewObject =
  | { type: "meeting"; data: Meeting }
  | { type: "agenda"; data: Agenda }
  | { type: "agendaModule"; data: AgendaModule }
  | { type: "agendaItem"; data: AgendaItem }
  | { type: "document"; data: Document };

export const useCreateTrackedNewObjects = () => {
  return useRef<TrackedNewObject[]>([]).current;
};

export const useTrackedNewObjects = () => {
  const array = useContext(TrackedNewObjectsContext);

  function trackNew(type: "meeting", data: Meeting | undefined): void;
  function trackNew(type: "agenda", data: Agenda | undefined): void;
  function trackNew(type: "agendaModule", data: AgendaModule | undefined): void;
  function trackNew(type: "agendaItem", data: AgendaItem | undefined): void;
  function trackNew(type: "document", data: Document | undefined): void;
  function trackNew(
    type: TrackedNewObject["type"],
    data: TrackedNewObject["data"] | undefined
  ): void {
    if (data) array?.push({ type, data } as TrackedNewObject);
  }
  return trackNew;
};

export const deleteTrackedNewObjects = async (
  trackedNew: TrackedNewObject[]
) => {
  const tenantId = tenantStore.getState().id;
  if (!tenantId) {
    throw new Error(
      "Trying to delete tracked new objects: Tenant not selected"
    );
  }

  // map TrackedNewObject["type"] to repository managers
  const repositoryMap = {
    meeting: meetingRepo,
    agenda: agendaRepo,
    agendaModule: agendaModuleRepo,
    agendaItem: agendaItemRepo,
    document: documentRepo,
  };

  // iterate and delete new objects
  for (const newObject of trackedNew) {
    const repo = repositoryMap[newObject.type];

    await repo.delete(tenantId, newObject.data.id);
  }
};
